function createDrawerElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function createDrawerCard(label, className = '') {
  const card = createDrawerElement('div', `cc-card${className ? ` ${className}` : ''}`);
  card.append(createDrawerElement('div', 'label', label));
  return card;
}

function createDrawerTable(headers, rows, emptyMessage) {
  const table = createDrawerElement('table', 'cc-table');
  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  headRow.append(...headers.map((header) => createDrawerElement('th', '', header)));
  head.append(headRow);
  const body = document.createElement('tbody');
  if (rows.length) {
    body.append(...rows);
  } else {
    const row = document.createElement('tr');
    const cell = createDrawerElement('td', 'muted', emptyMessage);
    cell.colSpan = headers.length;
    row.append(cell);
    body.append(row);
  }
  table.append(head, body);
  const scroll = createDrawerElement('div', 'cc-table-scroll');
  scroll.append(table);
  return scroll;
}

function createKeyValueRow(label, value, className = '') {
  const row = document.createElement('div');
  row.append(createDrawerElement('dt', '', label), createDrawerElement('dd', className, value));
  return row;
}

function createKpis(values) {
  const kpis = createDrawerElement('div', 'cc-kpis');
  values.forEach(([label, value]) => {
    const item = document.createElement('div');
    item.append(createDrawerElement('span', 'muted', label), createDrawerElement('strong', '', String(value)));
    kpis.append(item);
  });
  return kpis;
}

function createDiagnosticCounts(values, emptyText, formatLabel) {
  const entries = Object.entries(values || {});
  if (!entries.length) return createDrawerElement('div', 'muted', emptyText);
  return createKpis(entries.map(([label, value]) => [formatLabel(label), Number(value || 0)]));
}

export function ensureCommandCenterDrawer() {
  const existing = document.getElementById('cc-drawer');
  if (existing) return existing;
  const backdrop = createDrawerElement('div', 'cc-drawer-backdrop');
  backdrop.id = 'cc-drawer-backdrop';
  const drawer = createDrawerElement('aside', 'cc-drawer');
  drawer.id = 'cc-drawer';
  const head = createDrawerElement('div', 'cc-drawer-head');
  const title = createDrawerElement('h3', '', '详情');
  title.id = 'cc-drawer-title';
  const close = createDrawerElement('button', 'btn btn-sm', '关闭');
  close.type = 'button';
  close.id = 'cc-drawer-close';
  head.append(title, close);
  const body = createDrawerElement('div', 'cc-drawer-body');
  body.id = 'cc-drawer-body';
  drawer.append(head, body);
  document.body.append(backdrop, drawer);
  return drawer;
}

export function showCommandCenterDrawer(title, content) {
  ensureCommandCenterDrawer();
  const body = document.getElementById('cc-drawer-body');
  document.getElementById('cc-drawer-title').textContent = title || '详情';
  body.replaceChildren(content || document.createDocumentFragment());
  document.getElementById('cc-drawer').classList.add('open');
  document.getElementById('cc-drawer-backdrop').classList.add('open');
}

export function createCommandCenterDrawerMessage(className, text) {
  return createDrawerElement('div', className, text);
}

export function createTaskDiagnosticsContent(data, helpers) {
  const batch = data.batchJob || {};
  const listings = data.listingChain || {};
  const images = data.imageChain || {};
  const operations = data.operations || {};
  const fragment = document.createDocumentFragment();
  const keyValues = createDrawerElement('div', 'cc-kv');
  keyValues.append(
    createKeyValueRow('任务状态', `${helpers.statusLabel(batch.status)} · ${helpers.phaseLabel(batch.phase)}`),
    createKeyValueRow('用户', batch.userName || batch.userEmail || String(batch.userId || '')),
    createKeyValueRow('站点', batch.marketplace || '—'),
    createKeyValueRow('更新时间', helpers.formatCcTime(batch.updatedAt, 'datetime-sec')),
  );
  fragment.append(keyValues);
  if (batch.error) fragment.append(createDrawerElement('div', 'cc-error', batch.error));

  const listing = createDrawerCard('Listing 链路', 'cc-drawer-card');
  listing.append(createKpis([
    ['总数', Number(listings.total || 0)],
    ['有效 / 归档', `${Number(listings.active || 0)} / ${Number(listings.archived || 0)}`],
  ]));
  fragment.append(listing);

  const imageGrid = createDrawerElement('div', 'cc-grid-2 cc-drawer-section');
  const imageStatus = createDrawerCard('图片状态');
  imageStatus.append(createDiagnosticCounts(images.byStatus, '暂无图片状态', helpers.statusLabel));
  const imageProvider = createDrawerCard('图片服务');
  imageProvider.append(createDiagnosticCounts(images.byProvider, '暂无图片服务数据', helpers.providerLabel));
  imageGrid.append(imageStatus, imageProvider);
  fragment.append(imageGrid);

  const queueRows = (operations.queues || []).map((queue) => {
    const row = document.createElement('tr');
    row.append(
      createDrawerElement('td', '', queue.label || queue.key || ''),
      createDrawerElement('td', '', helpers.statusLabel(queue.status)),
      createDrawerElement('td', '', String(Number(queue.running || 0))),
      createDrawerElement('td', '', String(Number(queue.waiting || 0))),
    );
    return row;
  });
  const queues = createDrawerCard('队列诊断', 'cc-drawer-card');
  queues.append(
    createDrawerTable(['链路', '状态', '运行', '等待'], queueRows, '暂无队列诊断'),
    createDrawerElement('div', 'foot', operations.recovery?.reason || '当前没有恢复建议'),
  );
  fragment.append(queues);

  const imageRows = (images.recent || []).slice(0, 10).map((item) => {
    const provider = document.createElement('td');
    provider.append(
      document.createTextNode(helpers.providerLabel(item.provider)),
      createDrawerElement('div', 'mono', item.provider || ''),
    );
    const row = document.createElement('tr');
    row.append(
      createDrawerElement('td', '', String(item.id || '')),
      provider,
      createDrawerElement('td', '', item.model || '—'),
      createDrawerElement('td', '', helpers.statusLabel(item.status)),
    );
    return row;
  });
  const recent = createDrawerCard('近期图片记录', 'cc-drawer-card');
  recent.append(createDrawerTable(['编号', '图片服务', '模型', '状态'], imageRows, '暂无图片记录'));
  fragment.append(recent);
  return fragment;
}

export function createCommandCenterRecordContent(row, helpers) {
  const displayRow = {
    ...row,
    tsDisplay: helpers.formatCcTime(row.ts, 'datetime-sec'),
  };
  const fragment = document.createDocumentFragment();
  const keyValues = createDrawerElement('div', 'cc-kv');
  keyValues.append(
    createKeyValueRow('时间', helpers.formatCcTime(row.ts, 'datetime-sec')),
    createKeyValueRow('操作人', String(row.actor || '')),
    createKeyValueRow('业务模块', helpers.serviceLabel(row.module)),
    createKeyValueRow('执行结果', helpers.statusLabel(row.outcome)),
    createKeyValueRow('追踪编号', String(row.trace || ''), 'mono'),
  );
  fragment.append(
    keyValues,
    createDrawerElement('p', '', String(row.msg || '')),
    createDrawerElement('div', 'cc-code', JSON.stringify(displayRow, null, 2)),
    createDrawerElement('p', 'muted cc-drawer-followup', '可复制追踪编号继续检索关联记录。'),
  );
  return fragment;
}
